<template>
  <SloModal
    :router="!this.params"
    :maxWidth="440"
    v-if="authenticated || override || projects.length === 0"
    class="create-project"
    @formSubmit="create()"
    @dismiss="cancel()"
  >
    <template v-slot:title>
      {{ 'Create a DesignSpec' | t }}
    </template>
    <div class="content">
      <section v-stack-y:sm>
        <SloField>
          <SloLabel>{{ 'Spec Name' | t }}</SloLabel>
          <SloInput
            ref="input"
            type="text"
            v-model="name"
            :placeholder="'e.g. “Coworking Space”' | t"
          />
        </SloField>
        <p class="import" v-if="productIds.length > 0 || variationIds.length > 0">
          <strong>{{ productIds.length + variationIds.length }} {{ 'products' | t}}</strong>
          {{ 'will be imported from' | t }}
          <strong>{{ defaultName }}</strong>
        </p>
      </section>
    </div>
    <template v-slot:footer>
      <SloButton @click="cancel()" :disabled="loading" fill="outline" class="flex-1">
        {{ 'Cancel' | t }}
      </SloButton>
      <SloButton :disabled="!name || loading" type="submit" class="flex-1">
        <SloLoader :loading="loading" size="md" color="white">
          {{ 'Create' | t }}
        </SloLoader>
      </SloButton>
    </template>
  </SloModal>
  <SloModal :router="!this.params" :maxWidth="640" v-else class="create-project">
    <template v-slot:title>
      {{ 'Create a DesignSpec' | t }}
    </template>
    <div class="content">
      <section v-stack-y:md>
        <p>
          {{ 'You have already started a Design Spec project,' | t }}
          <strong>{{ projects[0].name }}</strong>. {{ 'Creating a new project will override your existing spec.' | t }}
        </p>
        <p>
          {{ 'To save your existing project, manage multiple projects, or share your specs with others,' | t }}
          <SloLink v-if="!this.$props.params" :to="{name: 'marketplace-auth-register'}">
            {{ 'Create an Account' | t }}
          </SloLink>
          <SloLink v-else :to="{name: 'marketplace-auth-register'}">
            {{ 'Create an Account' | t }}
          </SloLink>
          or
          <SloLink v-if="!this.$props.params" :to="{name: 'marketplace-auth-login'}">
            {{ 'Log In' | t }}
          </SloLink>
          <SloLink v-else :to="{name: 'marketplace-auth-login'}">
            {{ 'Log In' | t }}
          </SloLink>
          .
        </p>
      </section>
    </div>
    <template v-slot:footer>
      <SloButton fill="outline" @click="override = true">
        {{ 'Create new project' | t }}
      </SloButton>
      <router-link
        v-if="!params"
        :to="{ name: 'designspec-project', params: { id: projects[0].id } }"
      >
        <SloButton>
          {{ 'Go to existing project' | t }}
        </SloButton>
      </router-link>
      <SloButton v-else :href="'/designspec/project/' + projects[0].id">
        {{ 'Go to existing project' | t }}
      </SloButton>
    </template>
  </SloModal>
</template>

<script>
import {mapState, mapGetters, mapActions} from 'vuex';
import t from '../../../main/i18n';
import SloModal from '../../../../lib/components/SloModal.vue';
import SloInput from '../../../../lib/components/SloInput.vue';
import SloButton from '../../../../lib/components/SloButton.vue';
import SloLink from '../../../../lib/components/SloLink.vue';
import SloField from '../../../../lib/components/SloField.vue';
import SloLabel from '../../../../lib/components/SloLabel.vue';
import SloLoader from '../../../../lib/components/SloLoader.vue';

export default {
  components: {
    SloModal,
    SloInput,
    SloButton,
    SloLink,
    SloField,
    SloLabel,
    SloLoader
  },
  props: {
    redirectOnCreate: {type: Boolean, default: false},
    params: null,
    defaultName: {type: String, default: ''},
    coverImageId: {type: String, default: null},
    productIds: {type: Array, default: () => []},
    variationIds: {type: Array, default: () => []}
  },
  data() {
    return {
      loading: false,
      name: '',
      override: false
    };
  },
  filters: {
    t
  },
  mounted() {
    if (this.$props.defaultName) {
      this.name = this.$props.defaultName;
    }
    this.$nextTick(() => {
      this.$refs.input && this.$refs.input.setFocus();
    });
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    ...mapState('designspec', ['projects']),
    onCreate() {
      if (!this.$props.params) {
        if (this.redirectOnCreate) {
          return id =>
            this.$router.replace({
              name: 'designspec-project',
              params: {id: id}
            });
        }
      } else {
        if (this.$props.params.redirectOnCreate) {
          return id => (window.location = '/designspec/project/' + id);
        }
        return this.$props.params.onCreate;
      }
    }
  },
  methods: {
    ...mapActions('designspec', ['createProject', 'deleteProject', 'addSection']),
    cancel() {
      this.dismiss();
    },
    dismiss() {
      if (!this.params) {
        this.$router.replace('/designspec/projects');
      } else {
        this.$root.$emit('slo-modal:hide');
      }
    },
    async create() {
      if (this.loading) return;
      this.loading = true;
      if (this.override) {
        const existing = this.projects[0];
        if (existing) {
          await this.deleteProject(existing);
        }
      }

      this.override = true;
      try {
        const id = await this.createProject({
          name: this.name,
          productIds: this.productIds,
          variationIds: this.variationIds,
          coverImageId: this.coverImageId
        });

        if (!(!this.$props.params && this.redirectOnCreate)) {
          this.dismiss();
        }

        if (this.onCreate) {
          setTimeout(
            () =>
              this.$nextTick(() => {
                this.onCreate(id);
              }),
            500
          );
        }
      } catch (e) {
        this.$snotify.error(t('Unable to retrieve project details'));
      }
    }
  }
};
</script>

<style lang="scss">
@import '../../../../scss/system';

.create-project {
  min-height: 480px;

  .import {
    @include text('sm');
    background: $color-gray-200;
    padding: 8px 16px;
    border-radius: 4px;
    color: $color-gray-900;

    strong {
      font-weight: 500;
    }
  }

  .hint {
    display: flex;
    align-items: center;
    justify-content: center;

    @include text('sm');
    color: $color-gray-700;
  }
}
</style>
